import React, { useEffect, useMemo, useState } from 'react';
import { METHOD_META, METHOD_EXPLAIN, runAllMethods, seriesStats, explainPoint } from '../../utils/anomalyMethods';
import precomputed from '../../data/raporPrecomputed.json';
import AdminRaporIP3 from './AdminRaporIP3';
import AdminRaporIP4 from './AdminRaporIP4';
import AdminRaporIP5 from './AdminRaporIP5';
import AdminRaporCharts from './AdminRaporCharts';

const API = process.env.REACT_APP_API_BASE_URL || '';

const fmt = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '-';
  return Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2 });
};

const fmtDonem = (d) => {
  const s = String(d || '');
  if (s.length >= 6) return `${s.slice(4, 6)}.${s.slice(0, 4)}`;
  return s || '-';
};

const C = {
  blue: '#2563eb', green: '#16a34a', red: '#dc2626', orange: '#d97706', purple: '#7c3aed',
  text: '#1e293b', muted: '#64748b', border: '#e2e8f0', bg: '#f8fafc', card: '#fff',
};

const CAT = {
  'İstatistiksel': { bg: '#eff6ff', text: '#1e40af' },
  'Makine Öğrenmesi': { bg: '#fdf2f8', text: '#9d174d' },
  'Zaman Serisi': { bg: '#f0fdf4', text: '#166534' },
};

const isComplete = (r) =>
  r && r.aboneno && r.abone_adi && r.ilce_adi && r.mahalle_adi != null && r.m3_tuketim != null;

const thStyle = {
  textAlign: 'left', padding: '9px 11px', background: C.bg, borderBottom: `2px solid ${C.border}`,
  fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
};
const tdStyle = { padding: '8px 11px', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.text };

const loadFromBundle = () => {
  const parks = (precomputed.parks || []).map((p) => ({
    ...p,
    methods: p.methods || [],
    stats: p.stats || null,
    series: p.series || [],
  }));
  return {
    parks,
    meta: {
      generatedAt: precomputed.generatedAt,
      source: precomputed.source,
      mode: 'önceden-hesaplanmış',
    },
  };
};

async function fetchLiveParks() {
  const [verRes, aboneRes, periodsRes] = await Promise.all([
    fetch(`${API}/api/park-verification/parks?status=verified&limit=20`).then((r) => r.json()).catch(() => ({ parks: [] })),
    fetch(`${API}/api/aski/analysis/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groupBy: 'abone' }),
    }).then((r) => r.json()).catch(() => ({ rows: [] })),
    fetch(`${API}/api/aski/analysis/periods`).then((r) => r.json()).catch(() => ({ periods: [] })),
  ]);

  const latestPeriod = periodsRes.periods?.[0];
  let periodRows = [];
  if (latestPeriod) {
    const raw = await fetch(`${API}/api/aski/analysis/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        startPeriod: String(latestPeriod),
        endPeriod: String(latestPeriod),
        groupBy: 'raw',
      }),
    }).then((r) => r.json()).catch(() => ({ rows: [] }));
    periodRows = raw.rows || [];
  }

  const aboneMap = {};
  (aboneRes.rows || []).forEach((r) => { aboneMap[String(r.aboneno)] = r; });

  const picked = [];
  const used = new Set();

  for (const p of verRes.parks || []) {
    if (picked.length >= 5) break;
    const a = p.aski || {};
    const no = String(a.aboneno || '');
    if (!no || used.has(no)) continue;
    const agg = aboneMap[no] || {};
    picked.push({
      aboneno: no,
      abone_adi: a.abone_adi || agg.abone_adi || '-',
      ilce_adi: a.ilce_adi || agg.ilce_adi || '-',
      mahalle_adi: a.mahalle_adi || agg.mahalle_adi || '-',
      abonetipi: a.abonetipi || agg.abone_tipi || '-',
      toplam_tuketim: agg.toplam_tuketim,
      kaynak: 'Doğrulanmış',
    });
    used.add(no);
  }

  const fillers = periodRows
    .filter(isComplete)
    .filter((r) => /park/i.test(r.abone_adi || ''))
    .sort((a, b) => (parseFloat(b.m3_tuketim) || 0) - (parseFloat(a.m3_tuketim) || 0));

  for (const r of fillers) {
    if (picked.length >= 5) break;
    const no = String(r.aboneno);
    if (used.has(no)) continue;
    const agg = aboneMap[no] || {};
    picked.push({
      aboneno: no,
      abone_adi: r.abone_adi,
      ilce_adi: r.ilce_adi,
      mahalle_adi: r.mahalle_adi,
      abonetipi: r.abonetipi || '-',
      toplam_tuketim: agg.toplam_tuketim,
      kaynak: 'ASKİ',
    });
    used.add(no);
  }

  if (picked.length < 5) {
    for (const r of (aboneRes.rows || []).filter((x) => x.aboneno && x.abone_adi && x.ilce_adi)
      .sort((a, b) => (parseFloat(b.toplam_tuketim) || 0) - (parseFloat(a.toplam_tuketim) || 0))) {
      if (picked.length >= 5) break;
      const no = String(r.aboneno);
      if (used.has(no)) continue;
      picked.push({
        aboneno: no,
        abone_adi: r.abone_adi,
        ilce_adi: r.ilce_adi,
        mahalle_adi: r.mahalle_adi || '-',
        abonetipi: r.abone_tipi || '-',
        toplam_tuketim: r.toplam_tuketim,
        kaynak: 'ASKİ',
      });
      used.add(no);
    }
  }

  return Promise.all(
    picked.slice(0, 5).map(async (park) => {
      const hist = await fetch(`${API}/api/aski/analysis/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aboneno: park.aboneno }),
      }).then((r) => r.json()).catch(() => ({ rows: [] }));

      const series = (hist.rows || [])
        .map((row) => ({ donem: row.donem, m3: parseFloat(row.m3_tuketim) || 0 }))
        .filter((x) => x.donem != null)
        .sort((a, b) => String(a.donem).localeCompare(String(b.donem)));

      const byDonem = {};
      series.forEach((p) => {
        const k = String(p.donem);
        byDonem[k] = (byDonem[k] || 0) + p.m3;
      });
      const merged = Object.entries(byDonem)
        .map(([donem, m3]) => ({ donem, m3 }))
        .sort((a, b) => String(a.donem).localeCompare(String(b.donem)));

      return {
        ...park,
        series: merged,
        methods: runAllMethods(merged),
        stats: seriesStats(merged),
      };
    })
  );
}

const AdminRapor = () => {
  const initial = loadFromBundle();
  const [parks, setParks] = useState(initial.parks);
  const [meta, setMeta] = useState(initial.meta);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('zscore');
  const [selectedPark, setSelectedPark] = useState(initial.parks[0]?.aboneno || null);
  const [showOnlyAnomalies, setShowOnlyAnomalies] = useState(false);

  useEffect(() => {
    if (!selectedPark && parks[0]) setSelectedPark(parks[0].aboneno);
  }, [parks, selectedPark]);

  const refreshLive = async () => {
    try {
      setLoading(true);
      setError(null);
      const withSeries = await fetchLiveParks();
      setParks(withSeries);
      setMeta({
        generatedAt: new Date().toISOString(),
        source: 'Canlı ASKİ API + 13 metod (anlık yeniden hesap)',
        mode: 'canlı',
      });
      if (withSeries[0]) setSelectedPark(withSeries[0].aboneno);
    } catch (e) {
      setError(e.message || 'Canlı hesaplama başarısız');
    } finally {
      setLoading(false);
    }
  };

  const methodSummary = useMemo(() => {
    return METHOD_META.map((metaRow) => {
      const rows = parks.map((p) => {
        const m = (p.methods || []).find((x) => x.id === metaRow.id);
        return {
          aboneno: p.aboneno,
          abone_adi: p.abone_adi,
          ilce_adi: p.ilce_adi,
          stats: m?.stats || {},
          insufficient: m?.insufficient,
          anomalyCount: m?.stats?.anomalyCount || 0,
          mean: m?.stats?.mean,
          n: m?.stats?.n,
        };
      });
      return { ...metaRow, rows, totalAnomalies: rows.reduce((s, r) => s + (r.anomalyCount || 0), 0) };
    });
  }, [parks]);

  const activeMethod = methodSummary.find((m) => m.id === selectedMethod) || methodSummary[0];
  const activePark = parks.find((p) => p.aboneno === selectedPark) || parks[0];
  const activeParkMethod = activePark?.methods?.find((m) => m.id === selectedMethod);
  const explain = METHOD_EXPLAIN[selectedMethod] || precomputed.methodExplain?.[selectedMethod];

  const detailRows = useMemo(() => {
    if (!activeParkMethod?.points) return [];
    let pts = activeParkMethod.points;
    if (showOnlyAnomalies) pts = pts.filter((p) => p.isAnomaly);
    return pts;
  }, [activeParkMethod, showOnlyAnomalies]);

  const extraCols = useMemo(() => {
    if (!detailRows.length) return [];
    const skip = new Set(['donem', 'donemLabel', 'm3', 'isAnomaly']);
    return Object.keys(detailRows[0]).filter((k) => !skip.has(k));
  }, [detailRows]);

  const parkConsumptionSummary = useMemo(() => parks.map((p) => {
    const s = p.stats || {};
    const cv = s.mean > 0 ? (s.std / s.mean) * 100 : 0;
    const methodAnoms = (p.methods || []).reduce((acc, m) => acc + (m.stats?.anomalyCount || 0), 0);
    const methodsHit = (p.methods || []).filter((m) => (m.stats?.anomalyCount || 0) > 0).length;
    return {
      ...p,
      cv,
      methodAnoms,
      methodsHit,
      firstDonem: p.series?.[0]?.donem,
      lastDonem: p.series?.[p.series.length - 1]?.donem,
    };
  }), [parks]);

  const methodCatalog = useMemo(() => METHOD_META.map((meta) => {
    const ex = METHOD_EXPLAIN[meta.id] || {};
    const rows = parks.map((p) => {
      const m = (p.methods || []).find((x) => x.id === meta.id);
      return {
        aboneno: p.aboneno,
        abone_adi: p.abone_adi,
        n: m?.stats?.n,
        mean: m?.stats?.mean,
        anomalyCount: m?.stats?.anomalyCount || 0,
        rate: m?.stats?.n ? ((m.stats.anomalyCount || 0) / m.stats.n) * 100 : 0,
        params: m?.params || {},
        stats: m?.stats || {},
      };
    });
    return {
      ...meta,
      ...ex,
      rows,
      totalAnomalies: rows.reduce((s, r) => s + r.anomalyCount, 0),
    };
  }), [parks]);

  const selectedParkRawSeries = activePark?.series || [];

  const workedExample = useMemo(() => {
    if (!activeParkMethod?.points?.length) return null;
    const anom = activeParkMethod.points.find((p) => p.isAnomaly) || activeParkMethod.points[0];
    return {
      point: anom,
      note: explainPoint(selectedMethod, anom, activeParkMethod.stats, activeParkMethod.params),
    };
  }, [activeParkMethod, selectedMethod]);

  const generatedLabel = meta?.generatedAt
    ? new Date(meta.generatedAt).toLocaleString('tr-TR')
    : '-';

  return (
    <div style={{ padding: '20px 24px', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif', color: C.text }}>
      <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>Rapor</div>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            5 park • gerçek ASKİ m³ serisi • 13 anomali metodu (önceden hesaplanmış tablolar)
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            Kaynak: {meta?.source || '-'} · Hesap: {generatedLabel} · Mod: {meta?.mode || '-'}
          </div>
        </div>
        <button
          type="button"
          onClick={refreshLive}
          disabled={loading}
          style={{
            padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`,
            background: C.card, cursor: loading ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700, color: C.blue,
          }}
        >
          {loading ? 'Canlı hesaplanıyor…' : 'Canlı yeniden hesapla'}
        </button>
      </div>

      {error && (
        <div style={{ padding: 14, borderRadius: 8, background: '#fef2f2', color: '#b91c1c', fontSize: 13, marginBottom: 12 }}>{error}</div>
      )}

      {parks.length > 0 && (
        <>
          {/* 5 PARK ÖZET */}
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>1) Örnek Parklar (ASKİ) — konum + envanter</div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8, marginBottom: 12,
          }}>
            {[
              { k: 'ASKİ m³ serisi', v: 'Gerçek', ok: true },
              { k: '13 anomali metodu', v: 'Gerçek hesap', ok: true },
              { k: 'GPS doğrulanmış', v: `${parks.filter((p) => p.location?.hasGps).length}/5`, ok: parks.filter((p) => p.location?.hasGps).length >= 3 },
              { k: 'Mock veri', v: precomputed.mock ? 'Var' : 'Yok', ok: !precomputed.mock },
              { k: 'IP4 grafikler', v: 'ASKİ serisinden', ok: true },
              { k: 'IP3 uydu', v: 'GPS varsa GEE', ok: true },
            ].map((c) => (
              <div key={c.k} style={{
                background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 12px',
              }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>{c.k}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: c.ok ? C.green : C.orange, marginTop: 4 }}>{c.v}</div>
              </div>
            ))}
          </div>
          <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'auto', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['#', 'Abone No', 'Park / Abone Adı', 'İlçe', 'Mahalle', 'Sokak', 'Lat', 'Lng', 'GPS', 'İlk Dönem', 'Son Dönem', 'N', 'Ort.', 'Medyan', 'Std', 'CV%', 'Min', 'Max', 'Q1', 'Q3', 'Toplam m³', '13 metod anomali toplam', 'Anomali üreten metod', 'Kaynak'].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {parkConsumptionSummary.map((p, i) => (
                  <tr
                    key={p.aboneno}
                    onClick={() => setSelectedPark(p.aboneno)}
                    style={{ background: selectedPark === p.aboneno ? '#eff6ff' : (i % 2 ? C.bg : C.card), cursor: 'pointer' }}
                  >
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>{p.aboneno}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{p.abone_adi}</td>
                    <td style={tdStyle}>{p.ilce_adi}</td>
                    <td style={tdStyle}>{p.mahalle_adi}</td>
                    <td style={tdStyle}>{p.sokak_adi || '-'}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                      {p.location?.lat != null ? Number(p.location.lat).toFixed(6) : '-'}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                      {p.location?.lng != null ? Number(p.location.lng).toFixed(6) : '-'}
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700,
                        background: p.location?.hasGps ? '#dcfce7' : '#ffedd5',
                        color: p.location?.hasGps ? C.green : C.orange,
                      }}>
                        {p.location?.hasGps ? (p.location.coordSource || 'GPS') : 'Yok'}
                      </span>
                    </td>
                    <td style={tdStyle}>{fmtDonem(p.firstDonem)}</td>
                    <td style={tdStyle}>{fmtDonem(p.lastDonem)}</td>
                    <td style={tdStyle}>{p.stats?.n ?? '-'}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: C.blue }}>{fmt(p.stats?.mean)}</td>
                    <td style={tdStyle}>{fmt(p.stats?.median)}</td>
                    <td style={tdStyle}>{fmt(p.stats?.std)}</td>
                    <td style={tdStyle}>{fmt(p.cv)}</td>
                    <td style={tdStyle}>{fmt(p.stats?.min)}</td>
                    <td style={tdStyle}>{fmt(p.stats?.max)}</td>
                    <td style={tdStyle}>{fmt(p.stats?.q1)}</td>
                    <td style={tdStyle}>{fmt(p.stats?.q3)}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{fmt(p.stats?.total)}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: C.red }}>{p.methodAnoms}</td>
                    <td style={tdStyle}>{p.methodsHit}/13</td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700,
                        background: p.kaynak === 'Doğrulanmış' ? '#dcfce7' : '#dbeafe',
                        color: p.kaynak === 'Doğrulanmış' ? C.green : C.blue,
                      }}>{p.kaynak}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Ham tüketim serisi — seçili park */}
          {activePark && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                1b) Ham ASKİ tüketim serisi — {activePark.abone_adi}
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
                Her satır gerçek fatura dönemi m³. Toplam {activePark.series?.length || 0} dönem · ort {fmt(activePark.stats?.mean)} · std {fmt(activePark.stats?.std)}
              </div>
              <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'auto', maxHeight: 320 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['#', 'Dönem', 'Tüketim (m³)', 'Ortalamaya fark', 'Ortalamaya %', 'Kümülatif m³', 'Hesap notu'].map((h) => (
                        <th key={h} style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let cum = 0;
                      const mean = activePark.stats?.mean || 0;
                      return selectedParkRawSeries.map((s, i) => {
                        cum += s.m3;
                        const diff = s.m3 - mean;
                        const pct = mean ? (diff / mean) * 100 : 0;
                        return (
                          <tr key={`${s.donem}-${i}`} style={{ background: i % 2 ? C.bg : C.card }}>
                            <td style={tdStyle}>{i + 1}</td>
                            <td style={tdStyle}>{fmtDonem(s.donem)}</td>
                            <td style={{ ...tdStyle, fontWeight: 700 }}>{fmt(s.m3)}</td>
                            <td style={{ ...tdStyle, color: diff > 0 ? C.red : C.green }}>{fmt(diff)}</td>
                            <td style={tdStyle}>{fmt(pct)}%</td>
                            <td style={tdStyle}>{fmt(cum)}</td>
                            <td style={{ ...tdStyle, fontSize: 11, color: C.muted }}>
                              x={fmt(s.m3)} ; μ={fmt(mean)} ; x−μ={fmt(diff)}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 13 METOD KATALOĞU — formül + özet */}
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>2) 13 Anomali Metodu — formül kataloğu ve park özetleri</div>
          <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'auto', marginBottom: 16 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['#', 'Metod', 'Kategori', 'YZ?', 'Formül', 'Girdi', 'Çıktı', 'Adımlar', '5 park toplam anomali'].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {methodCatalog.map((m, i) => (
                  <tr
                    key={m.id}
                    onClick={() => setSelectedMethod(m.id)}
                    style={{ background: selectedMethod === m.id ? '#eff6ff' : (i % 2 ? C.bg : C.card), cursor: 'pointer' }}
                  >
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{m.name}</td>
                    <td style={tdStyle}>{m.category}</td>
                    <td style={tdStyle}>{m.ai ? 'Evet' : 'Hayır'}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, maxWidth: 280 }}>{m.formula || m.how}</td>
                    <td style={{ ...tdStyle, fontSize: 11, maxWidth: 140 }}>{m.inputs || 'm³ serisi'}</td>
                    <td style={{ ...tdStyle, fontSize: 11, maxWidth: 160 }}>{m.outputs || 'skor + isAnomaly'}</td>
                    <td style={{ ...tdStyle, fontSize: 11, maxWidth: 220 }}>{(m.steps || []).join(' → ')}</td>
                    <td style={{ ...tdStyle, fontWeight: 800, color: C.red }}>{m.totalAnomalies}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            {methodSummary.map((m) => {
              const active = selectedMethod === m.id;
              const cat = CAT[m.category] || { bg: '#f1f5f9', text: '#475569' };
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelectedMethod(m.id)}
                  style={{
                    padding: '8px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                    border: active ? `2px solid ${C.blue}` : `1px solid ${C.border}`,
                    background: active ? '#eff6ff' : C.card,
                    minWidth: 140,
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{m.name}</div>
                  <div style={{ fontSize: 10, color: cat.text, marginTop: 2 }}>{m.category}</div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Anomali: {m.totalAnomalies}</div>
                </button>
              );
            })}
          </div>

          {/* NASIL HESAPLANIYOR — geniş panel */}
          {activeMethod && explain && (
            <div style={{
              background: explain.ai ? '#fdf2f8' : '#f8fafc',
              border: `1px solid ${explain.ai ? '#fbcfe8' : C.border}`,
              borderRadius: 12, padding: '14px 16px', marginBottom: 16,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>
                  Nasıl hesaplanıyor? — {activeMethod.name}
                </div>
                <span style={{
                  padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                  background: explain.ai ? '#9d174d' : '#1e40af', color: '#fff',
                }}>
                  {explain.ai ? 'YZ / Makine Öğrenmesi' : 'İstatistik / Zaman Serisi'}
                </span>
              </div>
              <div style={{ fontSize: 13, color: C.text, marginTop: 8, lineHeight: 1.5 }}>{explain.how}</div>
              <div style={{
                marginTop: 10, padding: '10px 12px', borderRadius: 8, background: '#fff',
                border: `1px solid ${C.border}`, fontFamily: 'monospace', fontSize: 12,
              }}>
                Formül: {explain.formula || '—'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div style={{ fontSize: 12 }}><strong>Girdi:</strong> {explain.inputs || 'ASKİ m³ serisi'}</div>
                <div style={{ fontSize: 12 }}><strong>Çıktı:</strong> {explain.outputs || 'skor + anomali'}</div>
              </div>
              <ol style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                {(explain.steps || []).map((s) => <li key={s}>{s}</li>)}
              </ol>
              {workedExample && (
                <div style={{
                  marginTop: 12, padding: '10px 12px', borderRadius: 8,
                  background: workedExample.point.isAnomaly ? '#fef2f2' : '#f0fdf4',
                  border: `1px solid ${workedExample.point.isAnomaly ? '#fecaca' : '#bbf7d0'}`,
                  fontSize: 12,
                }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    Örnek hesap ({activePark?.abone_adi}) — dönem {workedExample.point.donemLabel || fmtDonem(workedExample.point.donem)} · x={fmt(workedExample.point.m3)} m³
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{workedExample.note}</div>
                  {activeParkMethod?.params && (
                    <div style={{ marginTop: 6, color: C.muted }}>
                      Parametreler: {Object.entries(activeParkMethod.params).map(([k, v]) => `${k}=${v}`).join(' · ') || '—'}
                    </div>
                  )}
                  {activeParkMethod?.stats && (
                    <div style={{ marginTop: 4, color: C.muted }}>
                      Seri istatistikleri: {Object.entries(activeParkMethod.stats).map(([k, v]) => `${k}=${fmt(v)}`).join(' · ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* METOD × PARK DETAYLI ÖZET */}
          {activeMethod && (
            <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'auto', marginBottom: 28 }}>
              <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{activeMethod.name} — park bazlı hesap özeti</div>
                <div style={{ fontSize: 11, color: C.muted }}>Tüm istatistikler ve anomali oranı (anomali/N)</div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Park', 'İlçe', 'N', 'Ort. m³', 'Anomali #', 'Anomali %', 'Parametreler', 'Tüm istatistikler (hesap çıktısı)'].map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeMethod.rows.map((r, i) => {
                    const parkObj = parks.find((p) => p.aboneno === r.aboneno);
                    const m = parkObj?.methods?.find((x) => x.id === selectedMethod);
                    const rate = r.n ? ((r.anomalyCount || 0) / r.n) * 100 : 0;
                    return (
                      <tr
                        key={r.aboneno}
                        onClick={() => setSelectedPark(r.aboneno)}
                        style={{ background: selectedPark === r.aboneno ? '#eff6ff' : (i % 2 ? C.bg : C.card), cursor: 'pointer' }}
                      >
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{r.abone_adi}</td>
                        <td style={tdStyle}>{r.ilce_adi}</td>
                        <td style={tdStyle}>{r.n ?? '-'}</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: C.blue }}>{fmt(r.mean)}</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: r.anomalyCount > 0 ? C.red : C.green }}>{r.anomalyCount}</td>
                        <td style={tdStyle}>{fmt(rate)}%</td>
                        <td style={{ ...tdStyle, fontSize: 11, fontFamily: 'monospace' }}>
                          {m?.params ? Object.entries(m.params).map(([k, v]) => `${k}=${v}`).join(' · ') : '-'}
                        </td>
                        <td style={{ ...tdStyle, fontSize: 11, color: C.muted, maxWidth: 420 }}>
                          {r.insufficient
                            ? 'Yetersiz dönem'
                            : Object.entries(r.stats || {}).map(([k, v]) => `${k}: ${fmt(v)}`).join(' · ')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* SEÇİLİ PARK + METOD DETAY TABLOSU */}
          {activePark && activeParkMethod && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>3) Dönem detayı + hesap — {activeParkMethod.name}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {activePark.abone_adi} ({activePark.aboneno}) • {activePark.series?.length || 0} dönem • her satırda tüketim + skor + hesap notu
                  </div>
                </div>
                <label style={{ fontSize: 12, color: C.muted, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={showOnlyAnomalies} onChange={(e) => setShowOnlyAnomalies(e.target.checked)} />
                  Sadece anomalileri göster
                </label>
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {parks.map((p) => (
                  <button
                    key={p.aboneno}
                    type="button"
                    onClick={() => setSelectedPark(p.aboneno)}
                    style={{
                      padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      border: selectedPark === p.aboneno ? `1px solid ${C.blue}` : `1px solid ${C.border}`,
                      background: selectedPark === p.aboneno ? '#dbeafe' : C.card,
                      color: selectedPark === p.aboneno ? C.blue : C.text,
                      maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                    title={p.abone_adi}
                  >
                    {p.abone_adi}
                  </button>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 12 }}>
                {[
                  { label: 'Ortalama m³', val: fmt(activeParkMethod.stats?.mean), color: C.blue },
                  { label: 'Anomali', val: activeParkMethod.stats?.anomalyCount ?? 0, color: C.red },
                  { label: 'Dönem (N)', val: activeParkMethod.stats?.n ?? 0, color: C.text },
                  { label: 'Anomali %', val: `${fmt(activeParkMethod.stats?.n ? (100 * (activeParkMethod.stats.anomalyCount || 0) / activeParkMethod.stats.n) : 0)}%`, color: C.orange },
                  { label: 'Std / Medyan', val: `${fmt(activePark.stats?.std)} / ${fmt(activePark.stats?.median)}`, color: C.purple || '#7c3aed' },
                ].map((c) => (
                  <div key={c.label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: 'uppercase' }}>{c.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: c.color, marginTop: 4 }}>{c.val}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'auto', maxHeight: 520, marginBottom: 28 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>#</th>
                      <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>Dönem</th>
                      <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>Tüketim (m³)</th>
                      <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>Anomali</th>
                      {extraCols.map((col) => (
                        <th key={col} style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>{col}</th>
                      ))}
                      <th style={{ ...thStyle, position: 'sticky', top: 0, zIndex: 1 }}>Hesap açıklaması</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((row, i) => (
                      <tr key={`${row.donem}-${i}`} style={{ background: row.isAnomaly ? '#fef2f2' : (i % 2 ? C.bg : C.card) }}>
                        <td style={tdStyle}>{i + 1}</td>
                        <td style={tdStyle}>{row.donemLabel || fmtDonem(row.donem)}</td>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>{fmt(row.m3)}</td>
                        <td style={tdStyle}>
                          {row.isAnomaly
                            ? <span style={{ color: C.red, fontWeight: 700 }}>Evet</span>
                            : <span style={{ color: C.green }}>Hayır</span>}
                        </td>
                        {extraCols.map((col) => (
                          <td key={col} style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>{fmt(row[col])}</td>
                        ))}
                        <td style={{ ...tdStyle, fontSize: 11, fontFamily: 'monospace', maxWidth: 360, whiteSpace: 'normal' }}>
                          {explainPoint(selectedMethod, row, activeParkMethod.stats, activeParkMethod.params)}
                        </td>
                      </tr>
                    ))}
                    {detailRows.length === 0 && (
                      <tr><td colSpan={5 + extraCols.length} style={{ ...tdStyle, textAlign: 'center', color: C.muted }}>Satır yok</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* TÜM METODLAR × TÜM PARKLAR — geniş özet */}
          <div style={{ fontSize: 15, fontWeight: 700, margin: '8px 0 10px' }}>3b) Tüm metodlar × tüm parklar — anomali sayısı ve oranı</div>
          <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'auto', marginBottom: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Metod</th>
                  <th style={thStyle}>Formül (kısa)</th>
                  {parks.map((p) => (
                    <th key={p.aboneno} style={{ ...thStyle, maxWidth: 130 }} title={p.abone_adi}>
                      {p.abone_adi.length > 16 ? `${p.abone_adi.slice(0, 16)}…` : p.abone_adi}
                      <div style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>N={p.stats?.n}</div>
                    </th>
                  ))}
                  <th style={thStyle}>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {methodCatalog.map((m, i) => (
                  <tr
                    key={m.id}
                    onClick={() => setSelectedMethod(m.id)}
                    style={{ background: selectedMethod === m.id ? '#eff6ff' : (i % 2 ? C.bg : C.card), cursor: 'pointer' }}
                  >
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{m.name}</td>
                    <td style={{ ...tdStyle, fontSize: 10, fontFamily: 'monospace', maxWidth: 200 }}>{m.formula || '-'}</td>
                    {m.rows.map((r) => (
                      <td key={r.aboneno} style={{ ...tdStyle, textAlign: 'center' }}>
                        <div style={{ fontWeight: 800, color: r.anomalyCount > 0 ? C.red : C.green }}>{r.anomalyCount}</div>
                        <div style={{ fontSize: 10, color: C.muted }}>{fmt(r.rate)}%</div>
                      </td>
                    ))}
                    <td style={{ ...tdStyle, fontWeight: 800 }}>{m.totalAnomalies}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TÜM METODLAR × TÜM PARKLAR MATRİS (eski numara 4) */}
          <div style={{ fontSize: 15, fontWeight: 700, margin: '28px 0 10px' }}>4) Metot × Park anomali matrisi</div>
          <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Metod</th>
                  <th style={thStyle}>Kategori</th>
                  {parks.map((p) => (
                    <th key={p.aboneno} style={{ ...thStyle, maxWidth: 120 }} title={p.abone_adi}>
                      {p.abone_adi.length > 18 ? `${p.abone_adi.slice(0, 18)}…` : p.abone_adi}
                    </th>
                  ))}
                  <th style={thStyle}>Toplam</th>
                </tr>
              </thead>
              <tbody>
                {methodSummary.map((m, i) => (
                  <tr
                    key={m.id}
                    onClick={() => setSelectedMethod(m.id)}
                    style={{ background: selectedMethod === m.id ? '#eff6ff' : (i % 2 ? C.bg : C.card), cursor: 'pointer' }}
                  >
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{m.name}</td>
                    <td style={{ ...tdStyle, fontSize: 11 }}>{m.category}</td>
                    {m.rows.map((r) => (
                      <td key={r.aboneno} style={{
                        ...tdStyle, fontWeight: 700, textAlign: 'center',
                        color: r.anomalyCount > 0 ? C.red : C.green,
                      }}>
                        {r.anomalyCount}
                      </td>
                    ))}
                    <td style={{ ...tdStyle, fontWeight: 800 }}>{m.totalAnomalies}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <AdminRaporCharts
            parks={parks}
            selectedPark={selectedPark}
            selectedMethod={selectedMethod}
            onSelectPark={setSelectedPark}
            onSelectMethod={setSelectedMethod}
          />
        </>
      )}

      <AdminRaporIP3 parks={parks} />
      <AdminRaporIP4 parks={parks} />
      <AdminRaporIP5 />
    </div>
  );
};

export default AdminRapor;
